import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';

// Selectors
import { selectLocationValues } from '../../../../../gql/selectors/actions';
import { AuthContextType } from '../../../../../hooks/useAuth';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { client } from '../../../../../gql/client';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';

const loadFormOptions = async (
  userData: AuthContextType | null,
  actionId: string
) => {
  try {
    if (userData?.user) {
      const locationValues = await selectLocationValues(actionId);
      return locationValues;
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Error loading location action data', err);
  }
};

export const LocationValidationLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async ({ params }) => {
  // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
  // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
  // cualquier `await` (diagnóstico 2026-10-08 § 8).
  reconcileValidationActionContext(params.id);
  // La lista de fotos y el croquis en curso también son de la actuación
  // de la URL. Las carpetas de OPFS no se tocan: las comparten las
  // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
  claimDraftScope(ACTION_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const stringifyForm = sessionStorage.getItem(
    formActionValidationNaming.NEW_VALIDATION_LOCATION_FORM
  );

  const stringifyActionData = sessionStorage.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  let sessionStorageData;
  let actionData;
  let parentExpeditionOrderStatus;
  let state;

  if (stringifyForm) {
    sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
  }

  if (stringifyActionData) {
    actionData = JSON.parse(decompressSessionStorage(stringifyActionData));
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: actionData.orderId,
      },
    });
    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
  } else if (params.id) {
    // BUG #5 FIX: When navigating via double-click, sessionStorage doesn't have actionData
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id: params.id },
    });

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: action.orderIDs[0] },
    });

    actionData = {
      id: params.id,
      orderId: order.id,
      roid: order.roid,
      isActionEdited: 'false',
    };

    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
  }

  const loaderToResolve = await loadFormOptions(userData, actionData?.id);

  const combinedData = {
    ...loaderToResolve,
    ...sessionStorageData,
    ...actionData,
    parentExpeditionOrderStatus,
    state,
    dataFromServer: loaderToResolve,
  };

  return combinedData;
};

  return { data: load() };
};
